// Rollup utilities for the new planner UI.
// Day-weighted rollups from the 12-week window into overlapping months.

import { deriveMonthlyMarkdownPercent, deriveMonthlyMarkdownValue, sumMonthlyMarkdownPlan } from './PlannerMonthlyMarkdown'

export const safeJsonParseArray = (s) => {
  if (!s) return []
  try {
    const v = JSON.parse(s)
    return Array.isArray(v) ? v : []
  } catch (e) {
    return []
  }
}

const toImuRate = (value) => {
  const n = Number(value || 0)
  if (!Number.isFinite(n) || n <= 0) return 0
  return n > 1 ? (n / 100) : n
}

export const deriveMonthlyInflows = (rows) => {
  const safeRows = Array.isArray(rows) ? rows : []
  if (!safeRows.length) return []

  return safeRows.map((row, index) => {
    const nextRow = safeRows[index + 1] || safeRows[0] || {}
    const currentBom = Number(row?.planInvBOM || 0)
    const nextBom = Number(nextRow?.planInvBOM || 0)
    const sales = Number(row?.salesPlan || 0)
    const markdowns = Number(row?.markdownPlan || 0)
    const planInflowRetail = Number((nextBom - currentBom + sales + markdowns).toFixed(2))
    const imuRate = toImuRate(row?.imuPlan)
    const planInflowCost = Number((planInflowRetail * (1 - imuRate)).toFixed(2))

    return {
      planInflowRetail,
      planInflowCost,
    }
  })
}

export const applyDerivedMonthlyInflows = (rows) => {
  const safeRows = Array.isArray(rows) ? rows : []
  const inflows = deriveMonthlyInflows(safeRows)

  return safeRows.map((row, index) => ({
    ...row,
    planInflowRetail: Number(inflows[index]?.planInflowRetail || 0),
    planInflowCost: Number(inflows[index]?.planInflowCost || 0),
  }))
}

const applyMonthlyMarkdownSharePercents = (rows) => {
  const list = Array.isArray(rows) ? rows : []
  const annualMarkdownPlan = sumMonthlyMarkdownPlan(list)
  return list.map((row) => ({
    ...row,
    markdownPlanPercent: deriveMonthlyMarkdownPercent({
      markdownPlan: Number(row?.markdownPlan ?? 0),
      annualMarkdownPlan,
    }),
  }))
}

/**
 * Build day counts for how each epochWeek contributes to each epochMonth.
 * Uses daily_values JSON objects like: { date, epoch_week, epoch_month, ... }
 */
export const buildWeekToMonthDayCountsFromDailyValues = (dailyValues) => {
  const weekToMonthDayCounts = new Map()
  ;(dailyValues || []).forEach((d) => {
    const ew = Number(d?.epoch_week)
    const em = Number(d?.epoch_month)
    if (!Number.isFinite(ew) || !Number.isFinite(em)) return
    if (!weekToMonthDayCounts.has(ew)) weekToMonthDayCounts.set(ew, new Map())
    const inner = weekToMonthDayCounts.get(ew)
    inner.set(em, (inner.get(em) || 0) + 1)
  })
  return { weekToMonthDayCounts }
}

/**
 * Allocate monthly plan dollars into overlapping weeks using each week's share of the month.
 * This is used by weekly seed/fallback display paths and must conserve the month total
 * across all weeks present in weekToMonthDayCounts.
 */
export const allocateMonthlyPlansToWeeksByMonthShare = ({ monthlyBaseUi, weekToMonthDayCounts }) => {
  const baseByEm = new Map()
  ;(monthlyBaseUi || []).forEach((m) => {
    const em = Number(m?.epochMonth)
    if (!Number.isFinite(em)) return
    baseByEm.set(em, {
      salesPlan: Number(m?.salesPlan ?? m?.sales_plan ?? m?.sales_monthly_plan ?? m?.salesMonthlyPlan ?? 0),
      markdownPlan: Number(m?.markdownPlan ?? m?.markdown_plan ?? m?.markdownMonthlyPlan ?? m?.markdowns_plan ?? 0),
    })
  })

  const monthDayTotals = new Map()
  for (const [, mMap] of (weekToMonthDayCounts || new Map()).entries()) {
    for (const [em, days] of mMap.entries()) {
      monthDayTotals.set(Number(em), (monthDayTotals.get(Number(em)) || 0) + Number(days || 0))
    }
  }

  const outByWeek = {}
  for (const [ew, mMap] of (weekToMonthDayCounts || new Map()).entries()) {
    let salesPlan = 0
    let markdownPlan = 0

    for (const [em, days] of mMap.entries()) {
      const month = baseByEm.get(Number(em))
      const monthDays = Number(monthDayTotals.get(Number(em)) || 0)
      if (!month || !monthDays) continue
      const w = Number(days || 0) / monthDays
      salesPlan += Number(month.salesPlan || 0) * w
      markdownPlan += Number(month.markdownPlan || 0) * w
    }

    outByWeek[String(ew)] = { salesPlan, markdownPlan }
  }

  return outByWeek
}

/**
 * Allocate-mode rollup (Mgmt):
 * - Annual (12-mo window) SALES is locked.
 * - Weekly changes increase the month(s) containing that week; months 2-12 offset proportionally.
 * - Inventory BOM is locked (unchanged when sales shifts).
 * - Markdowns plan $ is editable (NOT locked) and should roll up from weekly markdown edits.
 * - Inflow derived from sales; S/S recalculated from inv/sales.
 */
export const allocateWeeklyToMonthly = ({ monthlyBase, weeklySalesByMonthDelta, weeklyMarkdownsByMonthDelta }) => {
  const base = (monthlyBase || []).map((m) => ({ ...m }))
  if (!base.length) return base

  const baseSales = base.map((m) => Number(m?.salesPlan ?? 0))
  const baseMd = base.map((m) => Number(m?.markdownPlan ?? 0))
  const baseInv = base.map((m) => Number(m?.planInvBOM ?? 0))

  const annualBaseSales = baseSales.reduce((a, b) => a + b, 0)

  // 1) Sales: apply deltas into target months, then rebalance months 2-12 to keep annual locked.
  const newSales = baseSales.slice()
  for (const [emStr, delta] of Object.entries(weeklySalesByMonthDelta || {})) {
    const em = Number(emStr)
    const idx = base.findIndex((m) => Number(m?.epochMonth) === em)
    if (idx === -1) continue
    newSales[idx] = (newSales[idx] || 0) + (Number(delta) || 0)
  }

  const annualAfterWeek = newSales.reduce((a, b) => a + b, 0)
  const deltaAnnual = annualAfterWeek - annualBaseSales

  if (deltaAnnual !== 0 && base.length > 1) {
    const baseTailSum = baseSales.slice(1).reduce((a, b) => a + b, 0)
    if (baseTailSum !== 0) {
      const scale = (baseTailSum - deltaAnnual) / baseTailSum
      for (let i = 1; i < newSales.length; i++) {
        newSales[i] = (baseSales[i] || 0) * scale
      }
    } else {
      newSales[newSales.length - 1] = (newSales[newSales.length - 1] || 0) - deltaAnnual
    }
  }

  // 2) Markdowns: not locked. Apply deltas directly (no annual constraint).
  const newMd = baseMd.slice()
  for (const [emStr, delta] of Object.entries(weeklyMarkdownsByMonthDelta || {})) {
    const em = Number(emStr)
    const idx = base.findIndex((m) => Number(m?.epochMonth) === em)
    if (idx === -1) continue
    newMd[idx] = (newMd[idx] || 0) + (Number(delta) || 0)
  }

  const monthlyRows = base.map((m, idx) => {
    const salesPlan = Number(newSales[idx] || 0)
    const markdownPlan = Number(newMd[idx] || 0)
    const planInvBOM = Number(baseInv[idx] || 0)

    const planPercent = annualBaseSales ? (salesPlan / annualBaseSales) * 100 : 0
    const planSS = salesPlan ? (planInvBOM / salesPlan) : 0

    return {
      ...m,
      salesPlan,
      markdownPlan,
      markdownPlanPercent: 0,
      planPercent,
      planInvBOM,
      planSS,
    }
  })

  return applyDerivedMonthlyInflows(applyMonthlyMarkdownSharePercents(monthlyRows))
}

/**
 * Apply weekly edits to monthly plan values using day-overlap weights.
 * Precedence: latest timestamp wins (chronological order).
 */
export const rollupWeeklyToMonthly = ({ monthlyBase, monthlyEdits, weeklyEdits, weekToMonthDayCounts, weeklyActualSalesByWeek, calcMode = 'allocate' }) => {
  const byMonth = new Map()
  ;(monthlyBase || []).forEach((m) => {
    const em = Number(m?.epochMonth)
    if (!Number.isFinite(em)) return
    byMonth.set(em, { ...m })
  })

  const baseList = Array.from(byMonth.values()).sort((a, b) => Number(a.epochMonth) - Number(b.epochMonth))

  // Special Allocate-mode path (weekly edits impact month1, then re-balance months2-12).
  if (calcMode === 'allocate' && weeklyEdits && Object.keys(weeklyEdits).length) {
    const weeklySalesByMonthDelta = {}
    const weeklyMarkdownsByMonthDelta = {}
    const baseWeeklyAllocations = allocateMonthlyPlansToWeeksByMonthShare({
      monthlyBaseUi: baseList,
      weekToMonthDayCounts,
    })

    Object.entries(weeklyEdits || {}).forEach(([ewStr, edit]) => {
      const ew = Number(ewStr)
      if (!Number.isFinite(ew) || !edit) return
      const dayCounts = weekToMonthDayCounts?.get(ew)
      if (!dayCounts) return

      const targetEpochMonthSet = Array.isArray(edit?.targetEpochMonths) && edit.targetEpochMonths.length
        ? new Set(edit.targetEpochMonths.map((value) => String(value)))
        : null
      const scopedDayEntries = Array.from(dayCounts.entries()).filter(([em]) => {
        return !targetEpochMonthSet || targetEpochMonthSet.has(String(em))
      })
      const totalDays = scopedDayEntries.reduce((acc, [, days]) => acc + Number(days || 0), 0)
      if (!totalDays) return

      const weeklyActualSales = Number(weeklyActualSalesByWeek?.[ew] ?? 0)
      const weekSalesPlan = typeof edit?.salesPlan !== 'undefined'
        ? Number(edit.salesPlan || 0)
        : (Number(edit?.planPercent ?? 0) / 100) * weeklyActualSales

      // Weekly markdown $ derived from direct markdownPlan when available; otherwise from markdownPlanPercent.
      const weekMarkdownPlan = typeof edit?.markdownPlan !== 'undefined'
        ? Number(edit.markdownPlan || 0)
        : (Number(edit?.markdownPlanPercent ?? 0) / 100) * weekSalesPlan
      const baseWeekSalesPlan = Number(baseWeeklyAllocations?.[String(ew)]?.salesPlan ?? 0)
      const baseWeekMarkdownPlan = Number(baseWeeklyAllocations?.[String(ew)]?.markdownPlan ?? 0)

      for (const [em, days] of scopedDayEntries) {
        const w = days / totalDays
        weeklySalesByMonthDelta[String(em)] = (weeklySalesByMonthDelta[String(em)] || 0) + ((weekSalesPlan - baseWeekSalesPlan) * w)
        weeklyMarkdownsByMonthDelta[String(em)] = (weeklyMarkdownsByMonthDelta[String(em)] || 0) + ((weekMarkdownPlan - baseWeekMarkdownPlan) * w)
      }
    })

    const outList = allocateWeeklyToMonthly({
      monthlyBase: baseList,
      weeklySalesByMonthDelta,
      weeklyMarkdownsByMonthDelta,
    })
    const annualMarkdownBeforeEdits = sumMonthlyMarkdownPlan(outList)

    // Apply monthly edits on top.
    const outByMonth = new Map(outList.map((m) => [Number(m.epochMonth), m]))
    Object.entries(monthlyEdits || {}).forEach(([emStr, edit]) => {
      const em = Number(emStr)
      if (!Number.isFinite(em) || !edit) return
      const m = outByMonth.get(em)
      if (!m) return
      const out = { ...m }
      // Monthly edits are authoritative per latest ts.
      if (typeof edit.planPercent !== 'undefined') out.planPercent = Number(edit.planPercent)
      if (typeof edit.markdownPlanPercent !== 'undefined') out.markdownPlanPercent = Number(edit.markdownPlanPercent)
      if (typeof edit.planInvBOM !== 'undefined') out.planInvBOM = Number(edit.planInvBOM)
      if (typeof edit.planSS !== 'undefined') out.planSS = Number(edit.planSS)
      if (typeof edit.salesPlan !== 'undefined') out.salesPlan = Number(edit.salesPlan)
      if (typeof edit.markdownPlan !== 'undefined') out.markdownPlan = Number(edit.markdownPlan)
      else if (typeof edit.markdownPlanPercent !== 'undefined') {
        out.markdownPlan = deriveMonthlyMarkdownValue({
          markdownPlanPercent: Number(edit.markdownPlanPercent),
          annualMarkdownPlan: annualMarkdownBeforeEdits,
        })
      }
      if (typeof edit.imuPlan !== 'undefined') out.imuPlan = Number(edit.imuPlan)

      out._editTs = edit.ts || 0
      outByMonth.set(em, out)
    })

    return applyDerivedMonthlyInflows(
      applyMonthlyMarkdownSharePercents(Array.from(outByMonth.values()).sort((a, b) => Number(a.epochMonth) - Number(b.epochMonth)))
    )
  }

  const monthFromWeekly = new Map() // em -> { salesPlan, markdownPlan, invBom, ts }

  Object.entries(weeklyEdits || {}).forEach(([ewStr, edit]) => {
    const ew = Number(ewStr)
    if (!Number.isFinite(ew) || !edit) return
    const dayCounts = weekToMonthDayCounts?.get(ew)
    if (!dayCounts) return

    const targetEpochMonthSet = Array.isArray(edit?.targetEpochMonths) && edit.targetEpochMonths.length
      ? new Set(edit.targetEpochMonths.map((value) => String(value)))
      : null
    const scopedDayEntries = Array.from(dayCounts.entries()).filter(([em]) => {
      return !targetEpochMonthSet || targetEpochMonthSet.has(String(em))
    })
    const totalDays = scopedDayEntries.reduce((acc, [, days]) => acc + Number(days || 0), 0)
    if (!totalDays) return

    const weeklyActualSales = Number(weeklyActualSalesByWeek?.[ew] ?? 0)

    // Weekly-derived amounts from direct edited dollars when available, else editable percents.
    const salesPlanWeek = typeof edit?.salesPlan !== 'undefined'
      ? Number(edit.salesPlan || 0)
      : (Number(edit?.planPercent ?? 0) / 100) * weeklyActualSales
    const mdPlanWeek = typeof edit?.markdownPlan !== 'undefined'
      ? Number(edit.markdownPlan || 0)
      : (Number(edit?.markdownPlanPercent ?? 0) / 100) * salesPlanWeek

    for (const [em, days] of scopedDayEntries) {
      const w = days / totalDays
      if (!monthFromWeekly.has(em)) monthFromWeekly.set(em, { salesPlan: 0, markdownPlan: 0, invBom: null, ts: edit?.ts || 0 })
      const cur = monthFromWeekly.get(em)
      cur.salesPlan += salesPlanWeek * w
      cur.markdownPlan += mdPlanWeek * w
      cur.ts = Math.max(cur.ts || 0, edit?.ts || 0)
      if (typeof edit?.planInvBOM !== 'undefined' && edit?.planInvBOM !== null) {
        cur.invBom = Number(edit.planInvBOM)
      }
    }
  })

  const annualMarkdownBase = sumMonthlyMarkdownPlan(baseList)

  for (const [em, base] of byMonth.entries()) {
    const out = { ...base }

    const monthEdit = monthlyEdits?.[em] || null
    if (monthEdit) {
      if (typeof monthEdit.salesPlan !== 'undefined') out.salesPlan = Number(monthEdit.salesPlan)
      if (typeof monthEdit.markdownPlan !== 'undefined') out.markdownPlan = Number(monthEdit.markdownPlan)
      else if (typeof monthEdit.markdownPlanPercent !== 'undefined') {
        out.markdownPlan = deriveMonthlyMarkdownValue({
          markdownPlanPercent: Number(monthEdit.markdownPlanPercent),
          annualMarkdownPlan: annualMarkdownBase,
        })
      }
      if (typeof monthEdit.planInvBOM !== 'undefined') out.planInvBOM = Number(monthEdit.planInvBOM)
      if (typeof monthEdit.planPercent !== 'undefined') out.planPercent = Number(monthEdit.planPercent)
      if (typeof monthEdit.markdownPlanPercent !== 'undefined') out.markdownPlanPercent = Number(monthEdit.markdownPlanPercent)
      if (typeof monthEdit.planSS !== 'undefined') out.planSS = Number(monthEdit.planSS)
      if (typeof monthEdit.imuPlan !== 'undefined') out.imuPlan = Number(monthEdit.imuPlan)
      out._editTs = monthEdit.ts || 0
    }

    const weeklyDerived = monthFromWeekly.get(em) || null
    if (weeklyDerived && (weeklyDerived.ts || 0) >= (out._editTs || 0)) {
      out.salesPlan = weeklyDerived.salesPlan
      out.markdownPlan = weeklyDerived.markdownPlan
      if (weeklyDerived.invBom !== null) out.planInvBOM = weeklyDerived.invBom
      out._editTs = weeklyDerived.ts || 0
    }

    byMonth.set(em, out)
  }

  return applyDerivedMonthlyInflows(
    applyMonthlyMarkdownSharePercents(Array.from(byMonth.values()).sort((a, b) => Number(a.epochMonth) - Number(b.epochMonth)))
  )
}

// Prefer named exports.
// (No default export to keep linters happy.)
